import React from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { FluentProvider } from "@fluentui/react-components";
import {
  brandLightTheme,
  brandDarkTheme,
} from "theme/fluentBrandTheme";
import {
  AppSettingsProvider,
  useAppSettingsContext,
} from "contexts/AppSettingsProvider";
import { EAppSettings } from "library/types";
import { useAtom, useAtomValue } from "jotai";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ThemeProvider as StyledThemeProvider,
  styled,
} from "styled-components";
import "./App.css";
import AppHeader from "./components/AppHeader/AppHeader";

import { panelIsOpenState, darkModeState } from "./store";

import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { useResizeObserverDevErrorFix } from "hooks/useResizeObserverDevErrorFix";
import { allThemes } from "theme";
import { EThemeMode, EThemeName } from "theme/types";
import { GlobalFluentStyles } from "theme/GlobalFluentStyles";
import grecoTriangleBg from "./assets/GrECoGraphicTriangles.svg";
import { useGetTaxonomies } from "state/queries/useGetTaxonomies";
import Proposal from "pages/Proposal/Proposal";
import { routes } from "config/routes";
import ProposalList from "pages/ProposalList/ProposalList";
import {
  CascoConditionsPage,
  CascoConditionEditPage,
  InsurerEditPage,
} from "pages/Settings/CascoConditions";
import { UnauthorizedPage } from "pages/UnauthorizedPage/UnauthorizedPage";
import { NotFoundPage } from "pages/NotFoundPage/NotFoundPage";
import { MainProvider } from "providers/MainProvider";
import { useUserRoles } from "hooks/useUserRoles";

function AppWithTheme({ children }: { children: React.ReactNode }) {
  const { getSettingValue, isInitialized, hasAllSettings, isLoading } =
    useAppSettingsContext();
  const [, setDarkMode] = useAtom(darkModeState);

  const darkModeSetting = getSettingValue(EAppSettings.darkMode);
  const themeAppSetting = getSettingValue(EAppSettings.theme);

  const isDarkMode = darkModeSetting?.userAppSettingValue === "true";
  const selectedThemeName =
    themeAppSetting?.userAppSettingValue || EThemeName.main;

  // Sync with darkMode atom
  React.useEffect(() => {
    if (isInitialized && hasAllSettings) {
      setDarkMode(isDarkMode);
    }
  }, [isDarkMode, setDarkMode, isInitialized, hasAllSettings]);

  // Show loading until all settings are loaded and initialized (HBH pattern)
  if (isLoading || !isInitialized || !hasAllSettings) {
    const defaultStyledTheme = allThemes[EThemeName.main][EThemeMode.light];
    return (
      <FluentProvider theme={brandLightTheme}>
        <StyledThemeProvider theme={defaultStyledTheme}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <AppSpinner />
            <div>Loading application settings...</div>
          </div>
        </StyledThemeProvider>
      </FluentProvider>
    );
  }

  // Set up themes based on loaded settings
  const fluentTheme = isDarkMode ? brandDarkTheme : brandLightTheme;
  const styledTheme =
    allThemes[selectedThemeName as EThemeName]?.[
      isDarkMode ? EThemeMode.dark : EThemeMode.light
    ] || allThemes[EThemeName.main][EThemeMode.light];

  return (
    <FluentProvider theme={fluentTheme}>
      <StyledThemeProvider theme={styledTheme}>
        <GlobalFluentStyles />
        {children}
      </StyledThemeProvider>
    </FluentProvider>
  );
}

// Container component that has access to theme
const Container = styled("div")<{ $isPanelOpen: boolean }>`
  position: absolute;
  overflow: auto;
  top: 50px;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  background-color: ${(props) => props.theme.palette.themeBackground};
  background-image: url("${grecoTriangleBg}");
  background-size: auto;
  background-position-x: right;
  background-position-y: bottom;
  background-repeat: no-repeat;
`;

function App() {
  useResizeObserverDevErrorFix();
  const panelIsOpen = useAtomValue(panelIsOpenState);

  // Check if this is the invoice payment page BEFORE any providers load
  const isInvoicePaymentPage =
    window.location.pathname.startsWith("/invoice-payment/");

  // For invoice payment page, render completely independently without any providers
  if (isInvoicePaymentPage) {
    return (
      <div className="App">
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route
              path={routes.invoicePayment.path}
              element={<routes.invoicePayment.element />}
            />
            <Route
              path={routes.creditCardVerification.path}
              element={<routes.creditCardVerification.element />}
            />
            <Route
              path={routes.bankPaymentVerification.path}
              element={<routes.bankPaymentVerification.element />}
            />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App">
      <AppSettingsProvider>
        <AppWithTheme>
          <AppContent panelIsOpen={panelIsOpen} />
          <ReactQueryDevtools initialIsOpen={true} />
        </AppWithTheme>
      </AppSettingsProvider>
    </div>
  );
}

// Separate component that uses React Query hooks (must be inside QueryClientProvider)
function AppContent({ panelIsOpen }: { panelIsOpen: boolean }) {
  // Initialize user roles from bearer token
  const location = window.location;
  const is403Page = location.pathname === routes.unauthorizedPage.path;

  // Skip API call on unauthorized page to prevent infinite loop when /user/info returns 403
  useUserRoles(is403Page);

  const { data: taxonomies, isLoading } = useGetTaxonomies(!is403Page);

  // Show loading spinner while taxonomies are loading (unless on 403 page)
  if (!is403Page && (isLoading || !taxonomies)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <AppSpinner />
        <div>Loading taxonomies...</div>
      </div>
    );
  }

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppHeader />
      <Container $isPanelOpen={panelIsOpen}>
        <MainProvider>
          <Routes>
            <Route path={`${routes.offers.path}`} element={<ProposalList />} />
            <Route path={`${routes.offer.path}`} element={<Proposal />} />
            <Route path={routes.offerDetail.path} element={<Proposal />} />
            <Route
              path={routes.cascoConditions.path}
              element={<CascoConditionsPage />}
            />
            <Route
              path={routes.cascoConditionEdit.path}
              element={<CascoConditionEditPage />}
            />
            <Route
              path={routes.insurerEdit.path}
              element={<InsurerEditPage />}
            />
            <Route
              path={routes.unauthorizedPage.path}
              element={<UnauthorizedPage />}
            />
            <Route path={routes.notFoundPage.path} element={<NotFoundPage />} />
          </Routes>
        </MainProvider>
      </Container>
      <ToastContainer
        style={{
          zIndex: 9999999,
        }}
      />
    </BrowserRouter>
  );
}

export default App;
